import ReactPixel from 'react-facebook-pixel';

// Meta Pixel configuration
const PIXEL_ID = process.env.REACT_APP_FACEBOOK_PIXEL_ID || '1731410210896635';

// Check if we're on production domain
const isProductionDomain = () => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'helveticpadelseries.onrender.com' || 
         window.location.hostname === 'helveticpadelseries.ch' || 
         window.location.hostname === 'www.helveticpadelseries.ch';
};

// Initialize Meta Pixel (call once in App.js)
export const initializePixel = () => {
  if (!isProductionDomain()) {
    console.log('Meta Pixel: Skipping initialization (not on production domain)');
    return;
  }

  try {
    ReactPixel.init(PIXEL_ID, {}, {
      autoConfig: true,
      debug: false // Set to true for debugging if needed
    });
    console.log('✅ Meta Pixel initialized successfully');
    
    // Verify Pixel is loaded
    if (typeof window !== 'undefined' && window.fbq) {
      console.log('✅ Meta Pixel (fbq) is available');
    }
  } catch (error) {
    console.error('❌ Error initializing Meta Pixel:', error);
  }
};

// Track page view
export const trackPageView = () => {
  if (!isProductionDomain()) return;
  
  try {
    ReactPixel.pageView();
    console.log('Meta Pixel: Tracked PageView');
  } catch (error) {
    console.error('Error tracking PageView:', error);
  }
};

// Track custom event with deduplication support
export const trackEvent = (eventName, parameters = {}, eventId = null) => {
  if (!isProductionDomain()) {
    console.log(`Meta Pixel: Skipping event "${eventName}" (not on production domain)`);
    return null;
  }

  try {
    // Add event_id for deduplication if provided
    const eventParams = eventId ? { ...parameters, eventID: eventId } : parameters;
    
    ReactPixel.track(eventName, eventParams);
    
    console.log(`✅ Meta Pixel: Tracked event "${eventName}"`, {
      event_id: eventId || 'not_provided',
      parameters: Object.keys(eventParams)
    });
    
    return eventId;
  } catch (error) {
    console.error(`❌ Meta Pixel: Error tracking event "${eventName}":`, error);
    return null;
  }
};

// Track CompleteRegistration event (fires after successful payment)
// This should match the server-side event_id for deduplication
export const trackCompleteRegistration = (teamData, attribution = null, eventId = null) => {
  // Generate event ID if not provided (should match server-side)
  const finalEventId = eventId || generateEventId();
  
  const eventParams = {
    content_name: 'Team Registration',
    content_category: 'Registration',
    value: 160,
    currency: 'CHF',
    content_ids: [teamData.id || teamData.team_id],
    num_items: 1,
    eventID: finalEventId // For deduplication
  };

  // Add UTM parameters if available
  if (attribution) {
    if (attribution.utm_source) eventParams.utm_source = attribution.utm_source;
    if (attribution.utm_campaign) eventParams.utm_campaign = attribution.utm_campaign;
    if (attribution.utm_medium) eventParams.utm_medium = attribution.utm_medium;
    if (attribution.fbclid) eventParams.fbclid = attribution.fbclid;
  }

  // Add promo code if available
  if (teamData.promo_code) {
    eventParams.promo_code = teamData.promo_code.toUpperCase();
  }

  trackEvent('CompleteRegistration', eventParams, finalEventId);
  
  return finalEventId;
};

// Generate unique event ID (UUID v4)
const generateEventId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

// Track Purchase event (for backward compatibility)
export const trackPurchase = (amount, teamName, teamId, eventId = null) => {
  const finalEventId = eventId || generateEventId();
  
  trackEvent('Purchase', {
    value: amount,
    currency: 'CHF',
    content_name: 'Team Registration Fee',
    content_category: 'Payment',
    content_ids: [teamId],
    num_items: 1,
    eventID: finalEventId
  }, finalEventId);
  
  return finalEventId;
};

// Track Lead event (when registration form is submitted)
export const trackLead = (teamName, canton) => {
  trackEvent('Lead', {
    content_name: 'Team Registration',
    content_category: 'Registration',
    value: 1,
    currency: 'CHF'
  });
};

// Track ViewContent events
export const trackViewContent = (contentName, contentCategory) => {
  trackEvent('ViewContent', {
    content_name: contentName,
    content_category: contentCategory
  });
};

// Legacy functions for backward compatibility
export const trackTeamRegistrationConversion = (teamData) => {
  // Use CompleteRegistration instead
  return trackCompleteRegistration(teamData);
};

export const trackRegistration = (teamName, canton) => {
  trackLead(teamName, canton);
};

export const trackPayment = (amount, teamName) => {
  trackPurchase(amount, teamName);
};

export const trackContactForm = () => {
  trackEvent('Contact', {
    content_name: 'Contact Form Submission',
    content_category: 'Lead Generation'
  });
};

export const trackLeagueInfoView = () => {
  trackViewContent('League Information', 'Information');
};

export const trackRulesView = () => {
  trackViewContent('League Rules', 'Information');
};

export const trackPartnerClubsView = () => {
  trackViewContent('Partner Clubs', 'Information');
};

